'use client';

import React, { createContext, useContext, ReactNode } from 'react';
import { useOrderWorkflow, useOrderStatus } from '../hooks/useOrderWorkflow';
import { useCartManagement, useCartSummary } from '../hooks/useCartManagement';

/**
 * Order workflow context
 */
interface OrderWorkflowContextType {
  workflow: ReturnType<typeof useOrderWorkflow>;
  status: ReturnType<typeof useOrderStatus>;
}

const OrderWorkflowContext = createContext<OrderWorkflowContextType | null>(null);

/**
 * Cart management context
 */
interface CartManagementContextType {
  cart: ReturnType<typeof useCartManagement>;
  summary: ReturnType<typeof useCartSummary>;
}

const CartManagementContext = createContext<CartManagementContextType | null>(null);

/**
 * Combined state machine context
 */
interface StateMachineContextType {
  order: OrderWorkflowContextType;
  cart: CartManagementContextType;
}

const StateMachineContext = createContext<StateMachineContextType | null>(null);

/**
 * Order workflow provider
 */
export function OrderWorkflowProvider({ children }: { children: ReactNode }) {
  const workflow = useOrderWorkflow();
  const status = useOrderStatus();

  const value: OrderWorkflowContextType = {
    workflow,
    status,
  };

  return <OrderWorkflowContext.Provider value={value}>{children}</OrderWorkflowContext.Provider>;
}

/**
 * Cart management provider
 */
export function CartManagementProvider({ children }: { children: ReactNode }) {
  const cart = useCartManagement();
  const summary = useCartSummary();

  const value: CartManagementContextType = {
    cart,
    summary,
  };

  return <CartManagementContext.Provider value={value}>{children}</CartManagementContext.Provider>;
}

/**
 * Combined state machine provider
 */
export function StateMachineProvider({ children }: { children: ReactNode }) {
  return (
    <OrderWorkflowProvider>
      <CartManagementProvider>
        <StateMachineContextWrapper>{children}</StateMachineContextWrapper>
      </CartManagementProvider>
    </OrderWorkflowProvider>
  );
}

/**
 * Internal wrapper to combine contexts
 */
function StateMachineContextWrapper({ children }: { children: ReactNode }) {
  const order = useContext(OrderWorkflowContext);
  const cart = useContext(CartManagementContext);

  if (!order || !cart) {
    throw new Error('StateMachineContextWrapper must be used within StateMachineProvider');
  }

  const value: StateMachineContextType = {
    order,
    cart,
  };

  return <StateMachineContext.Provider value={value}>{children}</StateMachineContext.Provider>;
}

/**
 * Hook to use order workflow context
 */
export function useOrderWorkflowContext(): OrderWorkflowContextType {
  const context = useContext(OrderWorkflowContext);
  if (!context) {
    throw new Error('useOrderWorkflowContext must be used within OrderWorkflowProvider');
  }
  return context;
}

/**
 * Hook to use cart management context
 */
export function useCartManagementContext(): CartManagementContextType {
  const context = useContext(CartManagementContext);
  if (!context) {
    throw new Error('useCartManagementContext must be used within CartManagementProvider');
  }
  return context;
}

/**
 * Hook to use combined state machine context
 */
export function useStateMachineContext(): StateMachineContextType {
  const context = useContext(StateMachineContext);
  if (!context) {
    throw new Error('useStateMachineContext must be used within StateMachineProvider');
  }
  return context;
}

/**
 * Convenience hooks for specific state machine features
 */

// Order hooks
export function useOrderState() {
  const { workflow } = useOrderWorkflowContext();
  return {
    state: workflow.state,
    context: workflow.context,
    isLoading: workflow.isLoading,
    hasError: workflow.hasError,
    canSubmit: workflow.canSubmit,
    canRetry: workflow.canRetry,
  };
}

export function useOrderActions() {
  const { workflow } = useOrderWorkflowContext();
  return {
    startOrder: workflow.startOrder,
    submitOrder: workflow.submitOrder,
    processPayment: workflow.processPayment,
    confirmOrder: workflow.confirmOrder,
    cancelOrder: workflow.cancelOrder,
    retry: workflow.retry,
    reset: workflow.reset,
  };
}

export function useOrderStatusInfo() {
  const { status } = useOrderWorkflowContext();
  return {
    statusMessage: status.statusMessage,
    statusType: status.statusType,
    progress: status.progress,
  };
}

// Cart hooks
export function useCartState() {
  const { cart } = useCartManagementContext();
  return {
    items: cart.items,
    itemCount: cart.itemCount,
    total: cart.total,
    isEmpty: cart.isEmpty,
    isDirty: cart.isDirty,
    isLoading: cart.isLoading,
    hasError: cart.hasError,
    validation: cart.validation,
  };
}

export function useCartActions() {
  const { cart } = useCartManagementContext();
  return {
    addItem: cart.addItem,
    updateItem: cart.updateItem,
    removeItem: cart.removeItem,
    clearCart: cart.clearCart,
    updateItemQuantity: cart.updateItemQuantity,
    incrementItem: cart.incrementItem,
    decrementItem: cart.decrementItem,
    addOrUpdateItem: cart.addOrUpdateItem,
  };
}

export function useCartSyncState() {
  const { cart } = useCartManagementContext();
  return {
    isSyncing: cart.isSyncing,
    isSyncFailed: cart.isSyncFailed,
    isOffline: cart.isOffline,
    canSync: cart.canSync,
    canRetry: cart.canRetry,
    syncToStorage: cart.syncToStorage,
    retrySync: cart.retrySync,
  };
}

export function useCartSummaryInfo() {
  const { summary } = useCartManagementContext();
  return {
    formattedTotal: summary.formattedTotal,
    syncStatusMessage: summary.syncStatusMessage,
    syncStatusType: summary.syncStatusType,
    summary: summary.summary,
  };
}
